// =============================================
// FREE, client-side statement extraction (no API, no key, no per-doc cost).
//   - Digital PDFs  → pdfjs-dist text layer (fast, offline, accurate)
//   - Images / scanned PDFs → Tesseract.js browser OCR (slower, rougher)
// Then a heuristic parser turns the raw text into transaction rows. Output shape
// matches the Claude path so ConvertToCsvTab can swap engines transparently.
// The review grid is the safety net — local extraction is "good enough to edit".
// =============================================
import * as pdfjsLib from 'pdfjs-dist';
import { preprocessCanvasForOcr, imageToPreprocessedCanvas } from './ocrPreprocess';
// tesseract.js is lazy-loaded inside ocr() so it only downloads (and adds to the
// bundle) when an image/scanned PDF actually needs OCR.

pdfjsLib.GlobalWorkerOptions.workerSrc = `${process.env.PUBLIC_URL}/pdf.worker.min.js`;

export interface LocalTxn {
  date: string;
  description: string;
  amount: number;
  direction: 'debit' | 'credit';
  balance: number | null;
}
export interface LocalExtractResult {
  account_label: string;
  currency: string;
  transactions: LocalTxn[];
  source: 'browser_pdf' | 'browser_ocr';
}

const MONTHS: Record<string, number> = {
  jan: 1, feb: 2, mar: 3, apr: 4, may: 5, jun: 6, jul: 7, aug: 8, sep: 9, oct: 10, nov: 11, dec: 12,
};

const CREDIT_HINTS = /\b(deposit|payroll|credit|refund|interest|reversal|received|e-?transfer\s+in|incoming|rebate|cashback)\b/i;
const DEBIT_HINTS = /\b(withdrawal|purchase|payment|fee|pre-?auth|debit|pos|atm|bill|charge|transfer\s+out|nsf)\b/i;
const SKIP_LINE = /\b(opening|closing|beginning|ending|previous)\s+balance\b|\btotal\b|\bbalance\s+forward\b|\bstatement\s+period\b|\bpage\s+\d/i;

// ---- helpers ----
function pad(n: number): string { return String(n).padStart(2, '0'); }

function normYear(y: string): number {
  const n = parseInt(y, 10);
  return n < 100 ? 2000 + n : n;
}

/** Try several common statement date formats; return YYYY-MM-DD or ''. */
function extractDate(line: string, fallbackYear: number): { iso: string; matchText: string } | null {
  let m: RegExpMatchArray | null;

  // 2026-05-02
  m = line.match(/\b(\d{4})-(\d{2})-(\d{2})\b/);
  if (m) return { iso: `${m[1]}-${m[2]}-${m[3]}`, matchText: m[0] };

  // 05/02/2026 or 5-2-26  (assume M/D/Y; swap if first > 12)
  m = line.match(/\b(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{2,4})\b/);
  if (m) {
    let mo = parseInt(m[1], 10), d = parseInt(m[2], 10);
    if (mo > 12 && d <= 12) { const t = mo; mo = d; d = t; }
    if (mo >= 1 && mo <= 12 && d >= 1 && d <= 31) return { iso: `${normYear(m[3])}-${pad(mo)}-${pad(d)}`, matchText: m[0] };
  }

  // May 2, 2026  /  May 2
  m = line.match(/\b([A-Za-z]{3,9})\.?\s+(\d{1,2})(?:,?\s+(\d{2,4}))?\b/);
  if (m && MONTHS[m[1].slice(0, 3).toLowerCase()]) {
    const mo = MONTHS[m[1].slice(0, 3).toLowerCase()];
    const y = m[3] ? normYear(m[3]) : fallbackYear;
    return { iso: `${y}-${pad(mo)}-${pad(parseInt(m[2], 10))}`, matchText: m[0] };
  }

  // 2 May 2026  /  02 May
  m = line.match(/\b(\d{1,2})\s+([A-Za-z]{3,9})\.?(?:\s+(\d{2,4}))?\b/);
  if (m && MONTHS[m[2].slice(0, 3).toLowerCase()]) {
    const mo = MONTHS[m[2].slice(0, 3).toLowerCase()];
    const y = m[3] ? normYear(m[3]) : fallbackYear;
    return { iso: `${y}-${pad(mo)}-${pad(parseInt(m[1], 10))}`, matchText: m[0] };
  }
  return null;
}

const MONEY_RE = /\(?-?\$?\d[\d,]*\.\d{2}\)?/g;

function parseMoney(tok: string): { value: number; negative: boolean } {
  const negative = tok.includes('(') || tok.trim().startsWith('-');
  const value = parseFloat(tok.replace(/[(),$\s-]/g, ''));
  return { value: isNaN(value) ? 0 : value, negative };
}

function detectCurrency(text: string): string {
  if (/\bCAD\b|C\$/.test(text)) return 'CAD';
  if (/\bUSD\b|US\$/.test(text)) return 'USD';
  if (/\bGBP\b|£/.test(text)) return 'GBP';
  if (/\bEUR\b|€/.test(text)) return 'EUR';
  if (/\bINR\b|₹/.test(text)) return 'INR';
  if (/\bAUD\b|A\$/.test(text)) return 'AUD';
  if (/\$/.test(text)) return 'CAD';
  return 'CAD';
}

function detectAccountLabel(lines: string[]): string {
  for (const l of lines.slice(0, 12)) {
    if (/statement|account|chequing|checking|savings|visa|mastercard|credit\s+card/i.test(l) && l.length < 80) {
      return l.trim().replace(/\s{2,}/g, ' ');
    }
  }
  const masked = lines.find(l => /[*xX•]{2,}\s*\d{2,}/.test(l));
  return masked ? masked.trim().slice(0, 60) : '';
}

/** Heuristic statement-text parser. Imperfect by design — the grid fixes it. */
export function parseStatementText(text: string, source: LocalExtractResult['source']): LocalExtractResult {
  const rawLines = text.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
  const currency = detectCurrency(text);
  const account_label = detectAccountLabel(rawLines);

  // Best-guess year for dates missing one: first 4-digit year seen.
  const yearMatch = text.match(/\b(20\d{2})\b/);
  const fallbackYear = yearMatch ? parseInt(yearMatch[1], 10) : new Date().getFullYear();

  const transactions: LocalTxn[] = [];
  for (const line of rawLines) {
    if (SKIP_LINE.test(line)) continue;
    const d = extractDate(line, fallbackYear);
    if (!d) continue;

    const monies = line.match(MONEY_RE);
    if (!monies || monies.length === 0) continue;

    // amount = first money on the line; balance = last money if there are 2+
    const first = parseMoney(monies[0]);
    const balance = monies.length >= 2 ? parseMoney(monies[monies.length - 1]).value : null;

    // description = line minus the date and the money tokens
    let desc = line.replace(d.matchText, ' ');
    for (const mny of monies) desc = desc.replace(mny, ' ');
    desc = desc.replace(/\s{2,}/g, ' ').trim();
    if (!desc) desc = '(no description)';

    let direction: 'debit' | 'credit';
    if (first.negative) direction = 'debit';
    else if (CREDIT_HINTS.test(line)) direction = 'credit';
    else if (DEBIT_HINTS.test(line)) direction = 'debit';
    else direction = 'debit'; // most statement lines are debits; grid fixes outliers

    transactions.push({ date: d.iso, description: desc, amount: first.value, direction, balance });
  }

  return { account_label, currency, transactions, source };
}

// ---- PDF text-layer extraction (free, offline) ----
async function pdfText(buf: ArrayBuffer): Promise<string> {
  const pdf = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
  const out: string[] = [];
  const maxPages = Math.min(pdf.numPages, 15);
  for (let i = 1; i <= maxPages; i++) {
    const page = await pdf.getPage(i);
    const content = await page.getTextContent();
    // group items into lines by their y position so the parser sees real rows
    const rows: Record<string, { x: number; s: string }[]> = {};
    for (const it of content.items as any[]) {
      const y = Math.round(it.transform[5]);
      (rows[y] = rows[y] || []).push({ x: it.transform[4], s: it.str });
    }
    Object.keys(rows)
      .map(Number).sort((a, b) => b - a)
      .forEach(y => {
        const line = rows[y].sort((a, b) => a.x - b.x).map(p => p.s).join(' ').replace(/\s{2,}/g, ' ').trim();
        if (line) out.push(line);
      });
    out.push('');
    page.cleanup();
  }
  // Close the document, or its worker keeps the parsed file for the life of the tab.
  try { await pdf.destroy(); } catch { /* already closed */ }
  return out.join('\n');
}

// ---- render a scanned PDF page to a canvas for OCR ----
async function pdfPagesToCanvases(buf: ArrayBuffer, maxPages = 3): Promise<HTMLCanvasElement[]> {
  const pdf = await pdfjsLib.getDocument({ data: buf.slice(0) }).promise;
  const canvases: HTMLCanvasElement[] = [];
  const n = Math.min(pdf.numPages, maxPages);
  for (let i = 1; i <= n; i++) {
    const page = await pdf.getPage(i);
    const viewport = page.getViewport({ scale: 2 });
    const canvas = document.createElement('canvas');
    canvas.width = viewport.width; canvas.height = viewport.height;
    const ctx = canvas.getContext('2d')!;
    // intent 'print' = no requestAnimationFrame, so this doesn't stall in a hidden tab.
    await page.render({ canvasContext: ctx, viewport, intent: 'print' } as any).promise;
    preprocessCanvasForOcr(canvas);
    canvases.push(canvas);
    page.cleanup();
  }
  try { await pdf.destroy(); } catch { /* already closed */ }
  return canvases;
}

async function ocr(image: HTMLCanvasElement | File | Blob, onProgress?: (p: number) => void): Promise<string> {
  const Tesseract = (await import('tesseract.js')).default;
  const { data } = await Tesseract.recognize(image as any, 'eng', {
    logger: (m: any) => { if (m.status === 'recognizing text' && onProgress) onProgress(m.progress); },
  });
  return data.text || '';
}

/**
 * Extract a statement locally. PDFs try the text layer first; if it's empty
 * (a scan), fall back to OCR. Images go straight to OCR.
 */
export async function localExtractStatement(
  file: File,
  mediaType: string,
  onProgress?: (label: string, p: number) => void,
): Promise<LocalExtractResult> {
  const isPdf = mediaType === 'application/pdf';

  if (isPdf) {
    const buf = await file.arrayBuffer();
    onProgress?.('Reading PDF text…', 0.1);
    const text = await pdfText(buf);
    if (text.replace(/\s/g, '').length > 40) {
      return parseStatementText(text, 'browser_pdf');
    }
    // No usable text layer → scanned PDF → OCR the pages
    onProgress?.('Scanned PDF — running OCR…', 0.2);
    const canvases = await pdfPagesToCanvases(buf);
    let combined = '';
    for (let i = 0; i < canvases.length; i++) {
      combined += '\n' + await ocr(canvases[i], p => onProgress?.(`OCR page ${i + 1}/${canvases.length}…`, p));
    }
    return parseStatementText(combined, 'browser_ocr');
  }

  // image — clean + resolution-normalize before OCR
  onProgress?.('Running OCR…', 0.2);
  const cleaned = await imageToPreprocessedCanvas(file);
  const text = await ocr(cleaned, p => onProgress?.('Running OCR…', p));
  return parseStatementText(text, 'browser_ocr');
}
